import { Injectable } from '@angular/core';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions as UiNotificationActions } from '@next/core-lib/notification';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import * as WaterAuthoritiesActions from './water-authorities.actions';
import { WaterAuthorityDialogComponent, WaterAuthorityDialogData } from '../components/water-authority-dialog/water-authority-dialog.component';
import { WaterAuthoritiesService } from '../services/water-authorities.service';

@Injectable()
export class WaterAuthoritiesEffects {
  loadWaterAuthorities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WaterAuthoritiesActions.loadWaterAuthoritiesRequest),
      switchMap(() =>
        this.waterAuthoritiesService.getWaterAuthorities().pipe(
          map((waterAuthorities) => WaterAuthoritiesActions.loadWaterAuthoritiesSuccess({ waterAuthorities })),
          catchError((error) => {
            const errorMessage = this.localeService.translate('waterAuthorities.loadError');
            return of(
              UiNotificationActions.errorSnackbar({ message: errorMessage }),
              WaterAuthoritiesActions.loadWaterAuthoritiesFailure({ error: error.message }),
            );
          }),
        ),
      ),
    ),
  );

  upsertWaterAuthority$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WaterAuthoritiesActions.upsertWaterAuthorityRequest),
      switchMap((action) =>
        this.waterAuthoritiesService.upsertWaterAuthority(action.waterAuthority).pipe(
          map(() => {
            const successMessage = this.localeService.translate('waterAuthorities.saveSuccess');
            return [
              UiNotificationActions.successSnackbar({ message: successMessage }),
              WaterAuthoritiesActions.upsertWaterAuthoritySuccess({ waterAuthority: action.waterAuthority }),
              WaterAuthoritiesActions.loadWaterAuthoritiesRequest(),
            ];
          }),
          switchMap((actions) => actions),
          catchError((error) => {
            const errorMessage = this.localeService.translate('waterAuthorities.saveError');
            return of(
              UiNotificationActions.errorSnackbar({ message: errorMessage }),
              WaterAuthoritiesActions.upsertWaterAuthorityFailure({ error: error.message }),
            );
          }),
        ),
      ),
    ),
  );

  openCreateWaterAuthorityDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WaterAuthoritiesActions.openCreateWaterAuthorityDialog),
      switchMap(() => {
        const dialogData: WaterAuthorityDialogData = {
          title: this.localeService.translate('waterAuthorities.addWaterAuthority'),
        };

        return this.dialog.form(WaterAuthorityDialogComponent, dialogData).pipe(
          switchMap((result) => {
            if (result?.status === DIALOG_RETURN_TYPES.confirm) {
              return of(WaterAuthoritiesActions.upsertWaterAuthorityRequest({ waterAuthority: result.result! }));
            }
            return EMPTY;
          }),
        );
      }),
    ),
  );

  openEditWaterAuthorityDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WaterAuthoritiesActions.openEditWaterAuthorityDialog),
      switchMap((action) => {
        const dialogData: WaterAuthorityDialogData = {
          title: this.localeService.translate('waterAuthorities.editWaterAuthority'),
          waterAuthority: action.waterAuthority,
        };

        return this.dialog.form(WaterAuthorityDialogComponent, dialogData).pipe(
          switchMap((result) => {
            if (result?.status === DIALOG_RETURN_TYPES.confirm) {
              return of(WaterAuthoritiesActions.upsertWaterAuthorityRequest({ waterAuthority: result.result! }));
            }
            return EMPTY;
          }),
        );
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly waterAuthoritiesService: WaterAuthoritiesService,
    private readonly localeService: LocaleService,
    private readonly dialog: DialogService,
  ) {}
}
